/**
 * Shipment Service
 * 
 * Provides functionality to manage shipments in Firestore.
 * Handles CRUD operations, querying, and shipment grouping.
 * 
 * @module ShipmentService
 */

import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  writeBatch,
  QueryConstraint
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Shipment, ShipmentRow, ShipmentUpload, ShipmentFilters, ShipmentGroup, AssignedShipment, ArchivedShipment } from '../@types/shipment';
import { TrackingLinkService } from './TrackingLinkService';
import { getProductsByCodes } from './ProductService';
import { getSuppliesByCodes } from './SupplyService';

/**
 * Shipment Service Class
 * Handles all shipment-related database operations
 */
export class ShipmentService {
  private static readonly COLLECTION_NAME = 'shipments';
  private static readonly UPLOADS_COLLECTION = 'shipment_uploads';
  private static readonly ARCHIVED_COLLECTION = 'archived_shipments';
  private static readonly USERS_COLLECTION = 'users';
  private static readonly ASSIGNED_SHIPMENTS_SUBCOLLECTION = 'assigned_shipments';

  private static rowToShipment(
    row: ShipmentRow,
    uploadId: string,
    userId: string,
    productMap?: Map<string, any>,
    supplyMap?: Map<string, any>
  ): Omit<Shipment, 'id'> {
    let trackingId = (row.TrackingID || '').trim();
    const shipZone = (row.ShipZone || '').trim();
    
    // Always normalize Purolator tracking numbers if ShipZone indicates Purolator
    // This ensures we extract the correct tracking number even if carrier detection hasn't run yet
    const zoneUpper = shipZone.toUpperCase();
    if (zoneUpper.includes('PUROL') || zoneUpper.includes('PUROLATOR')) {
      const originalTrackingId = trackingId;
      const normalized = TrackingLinkService.extractPurolatorTrackingNumber(trackingId);
      
      // Always use normalized value if extraction found "609" or "610" + 9 digits pattern
      // If no pattern found, keep original (but log warning)
      if (normalized !== originalTrackingId) {
        console.log('[ShipmentService] Normalized Purolator tracking number:', {
          original: originalTrackingId,
          normalized: normalized
        });
        trackingId = normalized;
      } else if (originalTrackingId.length > 12 && !originalTrackingId.startsWith('904') && !originalTrackingId.startsWith('905')) {
        // Warn if long tracking number doesn't contain "609" or "610" pattern
        console.warn('[ShipmentService] Purolator tracking number does not contain "609/610" + 9 digits pattern:', {
          trackingId: originalTrackingId,
          shipZone: shipZone
        });
      }
    }
    
    // Always normalize Day & Ross tracking numbers if ShipZone indicates Day & Ross
    // Extract WPG + 7 digits if tracking ID starts with WPG
    if (zoneUpper.includes('DAYRO') || zoneUpper.includes('DAY ROSS') || zoneUpper.includes('DAY&ROSS') || zoneUpper.includes('DAYROSS')) {
      const originalTrackingId = trackingId;
      const normalized = TrackingLinkService.extractDayRossTrackingNumber(trackingId);
      
      if (normalized !== originalTrackingId) {
        console.log('[ShipmentService] Normalized Day & Ross tracking number:', {
          original: originalTrackingId,
          normalized: normalized
        });
        trackingId = normalized;
      }
    }
    
    // Detect carrier after normalization
    const carrier = TrackingLinkService.detectCarrier(shipZone, trackingId);
    
    const trackingUrl = TrackingLinkService.generateTrackingUrl(carrier, trackingId);

    // Build base shipment object
    const shipment: any = {
      uploadId,
      uploadedBy: userId,
      uploadedAt: Timestamp.now(),
      salesGroup: (row.SALES_GROUP || '').trim() || '',
      customerNo: (row.CustomerNo || '').trim(),
      invoiceNo: (row.InvoiceNo || '').trim(),
      salesOrderNo: (row.SalesOrderNo || '').trim(),
      orderDate: (row.OrderDate || '').trim(),
      shipToName: (row.ShipToName || '').trim(),
      shipToAddress1: (row.ShipToAddress1 || '').trim(),
      shipToCity: (row.ShipToCity || '').trim(),
      shipToState: (row.ShipToState || '').trim(),
      shipDate: (row.ShipDate || '').trim(),
      shipVia: (row.ShipVia || '').trim(),
      trackingId,
      shipZone,
      carrier,
      trackingUrl,
      warehouseCode: (row.WarehouseCode || '').trim(),
      quantityShipped: Number(row.QuantityShipped || 0),
      quantityOrdered: Number(row.QuantityOrdered || 0),
      quantityBackordered: Number(row.QuantityBackordered || 0),
      status: 'pending',
      sentToSalesReps: false,
      sentToUserIds: [],
      lastUpdated: Timestamp.now(),
      lastUpdatedBy: userId
    };

    // Add optional fields only if they have values (Firestore doesn't allow undefined)
    if (row.SalespersonNo?.trim()) {
      shipment.salespersonNo = row.SalespersonNo.trim();
    }
    if (row.CustomerPONo?.trim()) {
      shipment.customerPONo = row.CustomerPONo.trim();
    }
    if (row.PromiseDate?.trim()) {
      shipment.promiseDate = row.PromiseDate.trim();
    }
    if (row.STORE?.trim()) {
      shipment.store = row.STORE.trim();
    }
    if (row.TelephoneNo?.trim()) {
      shipment.telephoneNo = row.TelephoneNo.trim();
    }
    if (row.Pallets) {
      shipment.pallets = Number(row.Pallets);
    }
    if (row.Pieces) {
      shipment.pieces = Number(row.Pieces);
    }
    if (row.ProductLine?.trim()) {
      shipment.productLine = row.ProductLine.trim();
    }
    if (row.Category2?.trim()) {
      shipment.category2 = row.Category2.trim();
    }
    if (row.DetailSeqNo?.trim()) {
      shipment.detailSeqNo = row.DetailSeqNo.trim();
    }
    if (row.ItemCode?.trim()) {
      shipment.itemCode = row.ItemCode.trim();
    }
    if (row.ItemCodeDesc?.trim()) {
      shipment.itemCodeDesc = row.ItemCodeDesc.trim();
    }

    // Fetch product/supply names from Products or Supplies collection
    // Priority: Products first, then Supplies
    const code = row.ItemCode?.trim();
    if (code) {
      // Try Products collection first
      if (productMap) {
        const product = productMap.get(code);
        if (product?.ItemNameEN) {
          shipment.itemNameEN = product.ItemNameEN;
        }
        if (product?.ItemNameFR) {
          shipment.itemNameFR = product.ItemNameFR;
        }
      }
      
      // If not found in Products, try Supplies collection
      if (!shipment.itemNameEN && supplyMap) {
        const supply = supplyMap.get(code);
        if (supply) {
          // Map supply description fields to itemNameEN/itemNameFR
          shipment.itemNameEN = supply.descriptionEN || supply.description || undefined;
          shipment.itemNameFR = supply.descriptionFR || undefined;
        }
      }
    }

    return shipment as Omit<Shipment, 'id'>;
  }

  static async createShipments(
    shipments: ShipmentRow[],
    uploadId: string,
    userId: string
  ): Promise<void> {
    try {
      console.log('[ShipmentService] Creating shipments:', {
        count: shipments.length,
        uploadId,
        userId
      });

      // Collect all unique ItemCodes from shipments
      const itemCodes = new Set<string>();
      shipments.forEach(row => {
        if (row.ItemCode && row.ItemCode.trim()) {
          itemCodes.add(row.ItemCode.trim());
        }
      });

      // Batch fetch products and supplies for all ItemCodes
      let productMap = new Map<string, any>();
      let supplyMap = new Map<string, any>();
      
      if (itemCodes.size > 0) {
        const itemCodesArray = Array.from(itemCodes);
        console.log('[ShipmentService] Fetching products and supplies for', itemCodes.size, 'unique ItemCodes');
        
        // Fetch from both collections in parallel
        const [productsResult, suppliesResult] = await Promise.allSettled([
          getProductsByCodes(itemCodesArray),
          getSuppliesByCodes(itemCodesArray)
        ]);
        
        if (productsResult.status === 'fulfilled') {
          productMap = productsResult.value;
          console.log('[ShipmentService] Fetched', productMap.size, 'products from database');
        } else {
          console.warn('[ShipmentService] Error fetching products:', productsResult.reason);
        }
        
        if (suppliesResult.status === 'fulfilled') {
          supplyMap = suppliesResult.value;
          console.log('[ShipmentService] Fetched', supplyMap.size, 'supplies from database');
        } else {
          console.warn('[ShipmentService] Error fetching supplies:', suppliesResult.reason);
        }
      }

      const batch = writeBatch(db);
      const collectionRef = collection(db, this.COLLECTION_NAME);
      let batchCount = 0;
      const BATCH_SIZE = 500;

      for (const row of shipments) {
        const shipmentData = this.rowToShipment(row, uploadId, userId, productMap, supplyMap);
        const docRef = doc(collectionRef);
        batch.set(docRef, shipmentData);
        batchCount++;

        if (batchCount >= BATCH_SIZE) {
          await batch.commit();
          console.log('[ShipmentService] Committed batch of', batchCount, 'shipments');
          batchCount = 0;
        }
      }

      if (batchCount > 0) {
        await batch.commit();
        console.log('[ShipmentService] Committed final batch of', batchCount, 'shipments');
      }

      console.log('[ShipmentService] Successfully created', shipments.length, 'shipments');
    } catch (error: any) {
      console.error('[ShipmentService] Error creating shipments:', {
        error: error.message,
        stack: error.stack,
        uploadId,
        userId,
        shipmentCount: shipments.length
      });
      throw error;
    }
  }

  static async createUpload(uploadData: Omit<ShipmentUpload, 'id'>): Promise<string> {
    try {
      const docRef = doc(collection(db, this.UPLOADS_COLLECTION));
      const upload: ShipmentUpload = {
        id: docRef.id,
        ...uploadData
      };
      await setDoc(docRef, upload);
      console.log('[ShipmentService] Created upload record:', docRef.id);
      return docRef.id;
    } catch (error: any) {
      console.error('[ShipmentService] Error creating upload:', error);
      throw error;
    }
  }

  static async updateUpload(
    uploadId: string,
    updates: Partial<ShipmentUpload>
  ): Promise<void> {
    try {
      const docRef = doc(db, this.UPLOADS_COLLECTION, uploadId);
      await setDoc(docRef, updates, { merge: true });
      console.log('[ShipmentService] Updated upload:', uploadId);
    } catch (error: any) {
      console.error('[ShipmentService] Error updating upload:', error);
      throw error;
    }
  }

  static async getShipmentsBySalesGroup(salesGroup: string): Promise<Shipment[]> {
    try {
      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('salesGroup', '==', salesGroup),
        orderBy('shipDate', 'desc')
      );
      const snapshot = await getDocs(q);
      const shipments: Shipment[] = [];
      snapshot.forEach((doc) => {
        shipments.push({
          id: doc.id,
          ...doc.data()
        } as Shipment);
      });
      console.log('[ShipmentService] Retrieved', shipments.length, 'shipments for sales group:', salesGroup);
      return shipments;
    } catch (error: any) {
      console.error('[ShipmentService] Error getting shipments by sales group:', error);
      throw error;
    }
  }

  static async getShipmentsByTrackingId(trackingId: string): Promise<Shipment[]> {
    try {
      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('trackingId', '==', trackingId.trim())
      );
      const snapshot = await getDocs(q);
      const shipments: Shipment[] = [];
      snapshot.forEach((doc) => {
        shipments.push({
          id: doc.id,
          ...doc.data()
        } as Shipment);
      });
      shipments.sort((a, b) => {
        const seqA = Number(a.detailSeqNo || 0);
        const seqB = Number(b.detailSeqNo || 0);
        return seqA - seqB;
      });
      console.log('[ShipmentService] Retrieved', shipments.length, 'shipments for tracking ID:', trackingId);
      return shipments;
    } catch (error: any) {
      console.error('[ShipmentService] Error getting shipments by tracking ID:', error);
      throw error;
    }
  }

  static async getShipments(filters?: ShipmentFilters): Promise<Shipment[]> {
    try {
      const constraints: QueryConstraint[] = [];
      if (filters?.salesGroup) {
        constraints.push(where('salesGroup', '==', filters.salesGroup));
      }
      if (filters?.carrier) {
        constraints.push(where('carrier', '==', filters.carrier));
      }
      if (filters?.status) {
        constraints.push(where('status', '==', filters.status));
      }
      if (filters?.trackingId) {
        constraints.push(where('trackingId', '==', filters.trackingId.trim()));
      }
      if (filters?.customerNo) {
        constraints.push(where('customerNo', '==', filters.customerNo));
      }
      constraints.push(orderBy('shipDate', 'desc'));
      const q = query(collection(db, this.COLLECTION_NAME), ...constraints);
      const snapshot = await getDocs(q);
      const shipments: Shipment[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data() as Shipment;
        shipments.push({
          id: doc.id,
          ...data
        });
      });
      let filtered = shipments;
      if (filters?.shipDateFrom) {
        filtered = filtered.filter(s => {
          const shipDate = this.parseDate(s.shipDate);
          return shipDate >= filters.shipDateFrom!;
        });
      }
      if (filters?.shipDateTo) {
        filtered = filtered.filter(s => {
          const shipDate = this.parseDate(s.shipDate);
          return shipDate <= filters.shipDateTo!;
        });
      }
      if (filters?.searchQuery) {
        const query = filters.searchQuery.toLowerCase();
        filtered = filtered.filter(s =>
          s.trackingId.toLowerCase().includes(query) ||
          s.shipToName.toLowerCase().includes(query) ||
          s.customerNo.toLowerCase().includes(query) ||
          s.invoiceNo.toLowerCase().includes(query) ||
          s.salesOrderNo.toLowerCase().includes(query)
        );
      }
      console.log('[ShipmentService] Retrieved', filtered.length, 'shipments with filters');
      return filtered;
    } catch (error: any) {
      console.error('[ShipmentService] Error getting shipments:', error);
      throw error;
    }
  }

  static async updateShipment(
    shipmentId: string,
    updates: Partial<Shipment>,
    userId: string
  ): Promise<void> {
    try {
      if (updates.trackingId || updates.shipZone) {
        const docRef = doc(db, this.COLLECTION_NAME, shipmentId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const current = docSnap.data() as Shipment;
          let trackingId = updates.trackingId || current.trackingId;
          const shipZone = updates.shipZone || current.shipZone;
          
          // Normalize tracking ID based on carrier/shipZone before detecting carrier
          const zoneUpper = shipZone.toUpperCase();
          if (zoneUpper.includes('DAYRO') || zoneUpper.includes('DAY ROSS') || zoneUpper.includes('DAY&ROSS') || zoneUpper.includes('DAYROSS')) {
            trackingId = TrackingLinkService.extractDayRossTrackingNumber(trackingId);
          } else if (zoneUpper.includes('PUROL') || zoneUpper.includes('PUROLATOR')) {
            trackingId = TrackingLinkService.extractPurolatorTrackingNumber(trackingId);
          }
          
          const carrier = TrackingLinkService.detectCarrier(shipZone, trackingId);
          const trackingUrl = TrackingLinkService.generateTrackingUrl(carrier, trackingId);
          updates.trackingId = trackingId;
          updates.carrier = carrier;
          updates.trackingUrl = trackingUrl;
        }
      }
      const docRef = doc(db, this.COLLECTION_NAME, shipmentId);
      await setDoc(docRef, {
        ...updates,
        lastUpdated: Timestamp.now(),
        lastUpdatedBy: userId
      }, { merge: true });
      console.log('[ShipmentService] Updated shipment:', shipmentId);
    } catch (error: any) {
      console.error('[ShipmentService] Error updating shipment:', error);
      throw error;
    }
  }

  static async markAsSentToSalesReps(
    shipmentIds: string[],
    userId: string,
    salesRepIds: string[]
  ): Promise<void> {
    try {
      const batch = writeBatch(db);
      const now = Timestamp.now();
      for (const shipmentId of shipmentIds) {
        const docRef = doc(db, this.COLLECTION_NAME, shipmentId);
        batch.update(docRef, {
          sentToSalesReps: true,
          sentAt: now,
          sentToUserIds: salesRepIds,
          status: 'sent',
          lastUpdated: now,
          lastUpdatedBy: userId
        });
      }
      await batch.commit();
      console.log('[ShipmentService] Marked', shipmentIds.length, 'shipments as sent');
    } catch (error: any) {
      console.error('[ShipmentService] Error marking shipments as sent:', error);
      throw error;
    }
  }

  static groupShipmentsByTrackingId(shipments: Shipment[]): ShipmentGroup[] {
    const groups = new Map<string, { shipments: Shipment[]; originalTrackingId?: string }>();
    for (const shipment of shipments) {
      // Always normalize tracking ID for grouping (especially for Purolator)
      // This ensures even old shipments with long tracking numbers get normalized on display
      let normalizedTrackingId = shipment.trackingId.trim();
      let originalTrackingId: string | undefined = undefined;
      
      // Check if it's Purolator by carrier OR by ShipZone (in case carrier wasn't set correctly)
      const isPurolator = shipment.carrier === 'PUROLATOR' || 
                         (shipment.shipZone && (shipment.shipZone.toUpperCase().includes('PUROL') || 
                                                shipment.shipZone.toUpperCase().includes('PUROLATOR')));
      
      if (isPurolator) {
        const original = shipment.trackingId.trim();
        normalizedTrackingId = TrackingLinkService.extractPurolatorTrackingNumber(shipment.trackingId);
        // Track if correction was made (original was longer and different from normalized)
        if (normalizedTrackingId !== original && original.length > normalizedTrackingId.length) {
          originalTrackingId = original;
        }
      }
      
      // Check if it's Day & Ross by carrier OR by ShipZone (in case carrier wasn't set correctly)
      const isDayRoss = shipment.carrier === 'DAY_ROSS' ||
                       (shipment.shipZone && (shipment.shipZone.toUpperCase().includes('DAYRO') ||
                                             shipment.shipZone.toUpperCase().includes('DAY ROSS') ||
                                             shipment.shipZone.toUpperCase().includes('DAY&ROSS') ||
                                             shipment.shipZone.toUpperCase().includes('DAYROSS')));
      
      if (isDayRoss) {
        const original = shipment.trackingId.trim();
        const normalized = TrackingLinkService.extractDayRossTrackingNumber(shipment.trackingId);
        // Track if correction was made (original was longer and different from normalized)
        if (normalized !== original && original.length > normalized.length) {
          if (!originalTrackingId || original.length > originalTrackingId.length) {
            originalTrackingId = original;
          }
        }
        normalizedTrackingId = normalized;
      }
      
      const key = normalizedTrackingId;
      if (!groups.has(key)) {
        groups.set(key, { shipments: [], originalTrackingId });
      }
      const group = groups.get(key)!;
      group.shipments.push(shipment);
      // Keep the longest original tracking ID if multiple exist
      if (originalTrackingId && (!group.originalTrackingId || originalTrackingId.length > group.originalTrackingId.length)) {
        group.originalTrackingId = originalTrackingId;
      }
    }
    const result: ShipmentGroup[] = [];
    for (const [trackingId, groupData] of groups.entries()) {
      if (groupData.shipments.length === 0) continue;
      const first = groupData.shipments[0];
      const totalQuantity = groupData.shipments.reduce((sum, s) => sum + s.quantityShipped, 0);
      
      // Ensure tracking URL is generated with normalized tracking ID
      // Always regenerate for carriers with special URL logic (Purolator, Day & Ross)
      let trackingUrl = first.trackingUrl;
      if (first.carrier === 'PUROLATOR' || first.carrier === 'DAY_ROSS' || first.carrier === 'MIDLAND') {
        trackingUrl = TrackingLinkService.generateTrackingUrl(first.carrier, trackingId);
      } else if (trackingId !== first.trackingId) {
        trackingUrl = TrackingLinkService.generateTrackingUrl(first.carrier, trackingId);
      }
      
      result.push({
        trackingId, // Use normalized tracking ID for display
        carrier: first.carrier,
        trackingUrl,
        shipments: groupData.shipments.sort((a, b) => {
          const seqA = Number(a.detailSeqNo || 0);
          const seqB = Number(b.detailSeqNo || 0);
          return seqA - seqB;
        }),
        totalItems: groupData.shipments.length,
        totalQuantity,
        shipDate: first.shipDate,
        shipToName: first.shipToName,
        shipToAddress: first.shipToAddress1,
        shipToCity: first.shipToCity,
        shipToState: first.shipToState,
        originalTrackingId: groupData.originalTrackingId
      });
    }
    return result.sort((a, b) => {
      const dateA = this.parseDate(a.shipDate);
      const dateB = this.parseDate(b.shipDate);
      return dateB.getTime() - dateA.getTime();
    });
  }

  private static parseDate(dateString: string): Date {
    const parts = dateString.split('/');
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      const year = parseInt(parts[2], 10);
      return new Date(year, month, day);
    }
    const isoDate = new Date(dateString);
    if (!isNaN(isoDate.getTime())) {
      return isoDate;
    }
    return new Date();
  }

  static async getShipment(shipmentId: string): Promise<Shipment | null> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, shipmentId);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        } as Shipment;
      }
      return null;
    } catch (error: any) {
      console.error('[ShipmentService] Error getting shipment:', error);
      throw error;
    }
  }

  static async deleteShipment(shipmentId: string): Promise<void> {
    try {
      const docRef = doc(db, this.COLLECTION_NAME, shipmentId);
      await deleteDoc(docRef);
      console.log('[ShipmentService] Deleted shipment:', shipmentId);
    } catch (error: any) {
      console.error('[ShipmentService] Error deleting shipment:', error);
      throw error;
    }
  }

  static async deleteShipments(shipmentIds: string[]): Promise<void> {
    try {
      const batch = writeBatch(db);
      for (const shipmentId of shipmentIds) {
        const docRef = doc(db, this.COLLECTION_NAME, shipmentId);
        batch.delete(docRef);
      }
      await batch.commit();
      console.log('[ShipmentService] Deleted', shipmentIds.length, 'shipments');
    } catch (error: any) {
      console.error('[ShipmentService] Error deleting shipments:', error);
      throw error;
    }
  }

  static async deleteAllShipments(): Promise<void> {
    try {
      console.log('[ShipmentService] Deleting all shipments...');
      const snapshot = await getDocs(collection(db, this.COLLECTION_NAME));
      const batch = writeBatch(db);
      let batchCount = 0;
      const BATCH_SIZE = 500;

      snapshot.forEach((doc) => {
        batch.delete(doc.ref);
        batchCount++;

        if (batchCount >= BATCH_SIZE) {
          batch.commit();
          batchCount = 0;
        }
      });

      if (batchCount > 0) {
        await batch.commit();
      }

      console.log('[ShipmentService] Deleted all shipments');
    } catch (error: any) {
      console.error('[ShipmentService] Error deleting all shipments:', error);
      throw error;
    }
  }

  /**
   * Migrate existing Day & Ross shipments to normalize WPG tracking IDs
   * Extracts WPG + 7 digits from tracking IDs that start with WPG
   * 
   * @param userId - User ID performing the migration
   * @returns Number of shipments updated
   */
  static async migrateDayRossTrackingIds(userId: string): Promise<number> {
    try {
      console.log('[ShipmentService] Starting Day & Ross tracking ID migration...');
      
      // Get all shipments
      const snapshot = await getDocs(collection(db, this.COLLECTION_NAME));
      const shipmentsToUpdate: { id: string; originalTrackingId: string; normalizedTrackingId: string }[] = [];
      
      // Filter for Day & Ross shipments with WPG tracking IDs
      snapshot.forEach((doc) => {
        const data = doc.data() as Shipment;
        const trackingId = (data.trackingId || '').trim().toUpperCase();
        const shipZone = (data.shipZone || '').toUpperCase();
        const carrier = data.carrier;
        
        // Check if it's Day & Ross
        const isDayRoss = carrier === 'DAY_ROSS' ||
                         shipZone.includes('DAYRO') ||
                         shipZone.includes('DAY ROSS') ||
                         shipZone.includes('DAY&ROSS') ||
                         shipZone.includes('DAYROSS');
        
        // Check if tracking ID starts with WPG
        if (isDayRoss && trackingId.startsWith('WPG')) {
          const normalized = TrackingLinkService.extractDayRossTrackingNumber(trackingId);
          
          // Only update if normalization changed the tracking ID
          if (normalized !== trackingId) {
            shipmentsToUpdate.push({
              id: doc.id,
              originalTrackingId: trackingId,
              normalizedTrackingId: normalized
            });
          }
        }
      });
      
      console.log('[ShipmentService] Found', shipmentsToUpdate.length, 'Day & Ross shipments to update');
      
      if (shipmentsToUpdate.length === 0) {
        console.log('[ShipmentService] No shipments need updating');
        return 0;
      }
      
      // Update shipments in batches
      const batch = writeBatch(db);
      let batchCount = 0;
      const BATCH_SIZE = 500;
      let updatedCount = 0;
      
      for (const shipment of shipmentsToUpdate) {
        const docRef = doc(db, this.COLLECTION_NAME, shipment.id);
        const normalizedTrackingId = shipment.normalizedTrackingId;
        const carrier = 'DAY_ROSS';
        const trackingUrl = TrackingLinkService.generateTrackingUrl(carrier, normalizedTrackingId);
        
        batch.update(docRef, {
          trackingId: normalizedTrackingId,
          carrier: carrier,
          trackingUrl: trackingUrl,
          lastUpdated: Timestamp.now(),
          lastUpdatedBy: userId
        });
        
        batchCount++;
        updatedCount++;
        
        if (batchCount >= BATCH_SIZE) {
          await batch.commit();
          console.log('[ShipmentService] Committed batch of', batchCount, 'shipments');
          batchCount = 0;
        }
      }
      
      if (batchCount > 0) {
        await batch.commit();
        console.log('[ShipmentService] Committed final batch of', batchCount, 'shipments');
      }
      
      console.log('[ShipmentService] Successfully migrated', updatedCount, 'Day & Ross shipments');
      return updatedCount;
    } catch (error: any) {
      console.error('[ShipmentService] Error migrating Day & Ross tracking IDs:', error);
      throw error;
    }
  }

  /**
   * Create assigned shipment copy in user's assigned_shipments subcollection
   * 
   * @param userId - Sales rep user ID
   * @param shipment - Original shipment to copy
   * @param assignedBy - Manager user ID who assigned it
   * @returns Promise resolving to assigned shipment ID
   */
  static async createAssignedShipment(
    userId: string,
    shipment: Shipment,
    assignedBy: string
  ): Promise<string> {
    try {
      const assignedShipmentRef = doc(
        collection(db, this.USERS_COLLECTION, userId, this.ASSIGNED_SHIPMENTS_SUBCOLLECTION)
      );
      
      const assignedShipment: Omit<AssignedShipment, 'id'> = {
        ...shipment,
        assignedAt: Timestamp.now(),
        assignedBy,
        confirmed: false,
        originalShipmentId: shipment.id
      };

      await setDoc(assignedShipmentRef, assignedShipment);
      console.log('[ShipmentService] Created assigned shipment:', assignedShipmentRef.id, 'for user:', userId);
      return assignedShipmentRef.id;
    } catch (error: any) {
      console.error('[ShipmentService] Error creating assigned shipment:', error);
      throw error;
    }
  }

  /**
   * Get assigned shipments for a user
   * 
   * @param userId - Sales rep user ID
   * @returns Promise resolving to array of assigned shipments
   */
  static async getAssignedShipments(userId: string): Promise<AssignedShipment[]> {
    try {
      const q = query(
        collection(db, this.USERS_COLLECTION, userId, this.ASSIGNED_SHIPMENTS_SUBCOLLECTION),
        orderBy('shipDate', 'desc')
      );
      const snapshot = await getDocs(q);
      const shipments: AssignedShipment[] = [];
      snapshot.forEach((doc) => {
        shipments.push({
          id: doc.id,
          ...doc.data()
        } as AssignedShipment);
      });
      console.log('[ShipmentService] Retrieved', shipments.length, 'assigned shipments for user:', userId);
      return shipments;
    } catch (error: any) {
      console.error('[ShipmentService] Error getting assigned shipments:', error);
      throw error;
    }
  }

  /**
   * Delete assigned shipment
   * 
   * @param userId - Sales rep user ID
   * @param assignedShipmentId - Assigned shipment ID
   */
  static async deleteAssignedShipment(userId: string, assignedShipmentId: string): Promise<void> {
    try {
      const docRef = doc(
        db,
        this.USERS_COLLECTION,
        userId,
        this.ASSIGNED_SHIPMENTS_SUBCOLLECTION,
        assignedShipmentId
      );
      await deleteDoc(docRef);
      console.log('[ShipmentService] Deleted assigned shipment:', assignedShipmentId);
    } catch (error: any) {
      console.error('[ShipmentService] Error deleting assigned shipment:', error);
      throw error;
    }
  }

  /**
   * Confirm assigned shipment
   * 
   * @param userId - Sales rep user ID
   * @param assignedShipmentId - Assigned shipment ID
   */
  static async confirmAssignedShipment(userId: string, assignedShipmentId: string): Promise<void> {
    try {
      const docRef = doc(
        db,
        this.USERS_COLLECTION,
        userId,
        this.ASSIGNED_SHIPMENTS_SUBCOLLECTION,
        assignedShipmentId
      );
      await setDoc(docRef, {
        confirmed: true,
        confirmedAt: Timestamp.now(),
        lastUpdated: Timestamp.now(),
        lastUpdatedBy: userId
      }, { merge: true });
      console.log('[ShipmentService] Confirmed assigned shipment:', assignedShipmentId);
    } catch (error: any) {
      console.error('[ShipmentService] Error confirming assigned shipment:', error);
      throw error;
    }
  }

  /**
   * Archive shipments (move to archived_shipments collection)
   * Only archives shipments that are NOT assigned to sales reps
   * 
   * @param shipmentIds - Array of shipment IDs to archive
   * @param userId - User ID performing the archive
   */
  static async archiveShipments(shipmentIds: string[], userId: string): Promise<void> {
    try {
      const batch = writeBatch(db);
      const now = Timestamp.now();

      for (const shipmentId of shipmentIds) {
        // Get original shipment
        const shipmentRef = doc(db, this.COLLECTION_NAME, shipmentId);
        const shipmentDoc = await getDoc(shipmentRef);
        
        if (!shipmentDoc.exists()) {
          console.warn('[ShipmentService] Shipment not found for archiving:', shipmentId);
          continue;
        }

        const shipment = { id: shipmentDoc.id, ...shipmentDoc.data() } as Shipment;

        // Only archive if not sent to sales reps (to avoid affecting assigned shipments)
        if (!shipment.sentToSalesReps) {
          // Create archived shipment
          const archivedRef = doc(collection(db, this.ARCHIVED_COLLECTION));
          const archivedShipment: Omit<ArchivedShipment, 'id'> = {
            ...shipment,
            archivedAt: now,
            archivedBy: userId,
            originalShipmentId: shipmentId
          };
          batch.set(archivedRef, archivedShipment);

          // Delete original shipment
          batch.delete(shipmentRef);
        } else {
          console.log('[ShipmentService] Skipping shipment', shipmentId, '- already assigned to sales reps');
        }
      }

      await batch.commit();
      console.log('[ShipmentService] Archived', shipmentIds.length, 'shipments');
    } catch (error: any) {
      console.error('[ShipmentService] Error archiving shipments:', error);
      throw error;
    }
  }

  /**
   * Get archived shipments
   * 
   * @param filters - Optional filters
   * @returns Promise resolving to array of archived shipments
   */
  static async getArchivedShipments(filters?: ShipmentFilters): Promise<ArchivedShipment[]> {
    try {
      const constraints: QueryConstraint[] = [];
      if (filters?.salesGroup) {
        constraints.push(where('salesGroup', '==', filters.salesGroup));
      }
      if (filters?.carrier) {
        constraints.push(where('carrier', '==', filters.carrier));
      }
      if (filters?.status) {
        constraints.push(where('status', '==', filters.status));
      }
      constraints.push(orderBy('archivedAt', 'desc'));
      
      const q = query(collection(db, this.ARCHIVED_COLLECTION), ...constraints);
      const snapshot = await getDocs(q);
      const shipments: ArchivedShipment[] = [];
      
      snapshot.forEach((doc) => {
        shipments.push({
          id: doc.id,
          ...doc.data()
        } as ArchivedShipment);
      });

      // Apply client-side filters
      let filtered = shipments;
      if (filters?.searchQuery) {
        const query = filters.searchQuery.toLowerCase();
        filtered = filtered.filter(s =>
          s.trackingId.toLowerCase().includes(query) ||
          s.shipToName.toLowerCase().includes(query) ||
          s.customerNo.toLowerCase().includes(query) ||
          s.invoiceNo.toLowerCase().includes(query)
        );
      }

      console.log('[ShipmentService] Retrieved', filtered.length, 'archived shipments');
      return filtered;
    } catch (error: any) {
      console.error('[ShipmentService] Error getting archived shipments:', error);
      throw error;
    }
  }

  /**
   * Restore archived shipment (move back to shipments collection)
   * 
   * @param archivedShipmentId - Archived shipment ID
   * @param userId - User ID performing the restore
   */
  static async restoreArchivedShipment(archivedShipmentId: string, userId: string): Promise<void> {
    try {
      // Get archived shipment
      const archivedRef = doc(db, this.ARCHIVED_COLLECTION, archivedShipmentId);
      const archivedDoc = await getDoc(archivedRef);
      
      if (!archivedDoc.exists()) {
        throw new Error('Archived shipment not found');
      }

      const archivedShipment = archivedDoc.data() as ArchivedShipment;

      // Create new shipment document (don't use original ID to avoid conflicts)
      const shipmentRef = doc(collection(db, this.COLLECTION_NAME));
      const { archivedAt, archivedBy, originalShipmentId, ...shipmentData } = archivedShipment;
      
      const shipment: Omit<Shipment, 'id'> = {
        ...shipmentData,
        status: 'pending',
        sentToSalesReps: false,
        sentToUserIds: [],
        lastUpdated: Timestamp.now(),
        lastUpdatedBy: userId
      };

      const batch = writeBatch(db);
      batch.set(shipmentRef, shipment);
      batch.delete(archivedRef);
      await batch.commit();

      console.log('[ShipmentService] Restored archived shipment:', archivedShipmentId);
    } catch (error: any) {
      console.error('[ShipmentService] Error restoring archived shipment:', error);
      throw error;
    }
  }

  /**
   * Delete shipments that are NOT assigned to sales reps
   * 
   * @param shipmentIds - Array of shipment IDs to delete
   */
  static async deleteUnassignedShipments(shipmentIds: string[]): Promise<void> {
    try {
      const batch = writeBatch(db);
      let deletedCount = 0;

      for (const shipmentId of shipmentIds) {
        const docRef = doc(db, this.COLLECTION_NAME, shipmentId);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const shipment = docSnap.data() as Shipment;
          // Only delete if not sent to sales reps
          if (!shipment.sentToSalesReps) {
            batch.delete(docRef);
            deletedCount++;
          } else {
            console.log('[ShipmentService] Skipping deletion of shipment', shipmentId, '- already assigned to sales reps');
          }
        }
      }

      if (deletedCount > 0) {
        await batch.commit();
        console.log('[ShipmentService] Deleted', deletedCount, 'unassigned shipments');
      } else {
        console.log('[ShipmentService] No unassigned shipments to delete');
      }
    } catch (error: any) {
      console.error('[ShipmentService] Error deleting unassigned shipments:', error);
      throw error;
    }
  }

  /**
   * Delete all shipments that are NOT assigned to sales reps
   */
  static async deleteAllUnassignedShipments(): Promise<void> {
    try {
      console.log('[ShipmentService] Deleting all unassigned shipments...');
      const snapshot = await getDocs(collection(db, this.COLLECTION_NAME));
      const batch = writeBatch(db);
      let batchCount = 0;
      let deletedCount = 0;
      const BATCH_SIZE = 500;

      snapshot.forEach((doc) => {
        const shipment = doc.data() as Shipment;
        // Only delete if not sent to sales reps
        if (!shipment.sentToSalesReps) {
          batch.delete(doc.ref);
          deletedCount++;
          batchCount++;

          if (batchCount >= BATCH_SIZE) {
            batch.commit();
            batchCount = 0;
          }
        }
      });

      if (batchCount > 0) {
        await batch.commit();
      }

      console.log('[ShipmentService] Deleted', deletedCount, 'unassigned shipments');
    } catch (error: any) {
      console.error('[ShipmentService] Error deleting all unassigned shipments:', error);
      throw error;
    }
  }
}
